import EastIcon from "@mui/icons-material/East";
import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Theme,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { motion } from "framer-motion";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getRelativeFontSize, regularFont } from "utils/styles";
import emailIcon from "../../assets/icons/emailIcon.svg";
import IISBlack from "../../assets/images/IISBlack.png";
import IISWhite from "../../assets/images/IISWhite.png";
import PanaceaBlack from "../../assets/images/PanaceaBlack.png";
import PanaceaWhite from "../../assets/images/PanaceaWhite.png";
import aboutImage from "../../assets/images/about-image-1.svg";
import { getContactStyles } from "./Contact.styles";
import { submitContactForm } from "./ContactServices";

const BRAND = "#f8481c";
const BRAND_LIGHT = "#ff6b4a";

const getImageSrc = (img: string | { default?: string }): string =>
  typeof img === "string" ? img : (img?.default ?? "");

const OTHER_WEBSITES = [
  {
    id: "iis-usa",
    name: "IIS USA",
    description:
      "IIS USA delivers expertise in international standards, compliance, and strategic consulting. Connect with our team for insights on quality, sustainability, and global best practices.",
    websiteUrl: "https://iisusa.org",
    logoLight: IISWhite,
    logoDark: IISBlack,
    image: aboutImage,
  },
  {
    id: "panacea-project",
    name: "Panacea Project",
    description:
      "Panacea Project focuses on innovative solutions for wellness and sustainable living. Explore research, initiatives, and community-driven programs aimed at holistic health and environmental balance.",
    websiteUrl: "https://panaceaproject.com",
    logoLight: PanaceaWhite,
    logoDark: PanaceaBlack,
    image: aboutImage,
  },
];

const initialState = {
  first_name: { value: "", error: "" },
  last_name: { value: "", error: "" },
  email: { value: "", error: "" },
  subject: { value: "", error: "" },
  message: { value: "", error: "" },
};

const textFieldSx = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";
  return {
    mb: 2,
    "& .MuiOutlinedInput-root": {
      ...regularFont,
      fontSize: getRelativeFontSize(0),
      backgroundColor: isDark
        ? alpha(theme.palette.background.default, 0.5)
        : alpha(theme.palette.background.paper, 0.9),
      borderRadius: "12px",
      "& fieldset": {
        borderWidth: 1.5,
        borderColor: isDark
          ? alpha(theme.palette.divider, 0.5)
          : alpha(theme.palette.common.black, 0.22),
      },
      "&:hover fieldset": {
        borderColor: alpha(BRAND, 0.6),
      },
      "&.Mui-focused fieldset": {
        borderColor: BRAND,
        borderWidth: 2,
        boxShadow: `0 0 0 4px ${alpha(BRAND, 0.15)}`,
      },
    },
    "& .MuiInputBase-input": {
      color: theme.palette.text.primary,
      "&::placeholder": {
        fontSize: getRelativeFontSize(0),
        opacity: 0.8,
      },
    },
  };
};

const Contact = () => {
  const theme = useTheme();
  const classes = getContactStyles(theme);
  const { t } = useTranslation();
  const [formFields, setFormFields] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const contactEmail = "info@iisusa.org";

  const handleSayHello = () => {
    window.location.href = `mailto:${contactEmail}`;
  };

  const handleVisitWebsite = (url: string) => {
    window.open(url, "_blank");
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const name = e.target.name as keyof typeof formFields;
    if (!(name in formFields)) return;
    setFormFields((prev) => ({
      ...prev,
      [name]: { value: e.target.value, error: "" },
    }));
  };

  const validate = () => {
    const next = { ...formFields };
    let valid = true;
    if (!formFields.first_name.value.trim()) {
      next.first_name.error = "First name is required";
      valid = false;
    }
    if (!formFields.last_name.value.trim()) {
      next.last_name.error = "Last name is required";
      valid = false;
    }
    if (!formFields.email.value.trim()) {
      next.email.error = "Email is required";
      valid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(formFields.email.value)) {
      next.email.error = "Invalid email";
      valid = false;
    }
    if (!formFields.subject.value.trim()) {
      next.subject.error = "Subject is required";
      valid = false;
    }
    if (!formFields.message.value.trim()) {
      next.message.error = "Message is required";
      valid = false;
    }
    setFormFields(next);
    if (!valid)
      openErrorNotification("Please fill all required fields correctly.");
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setIsLoading(true);
      const response = await submitContactForm({
        first_name: formFields.first_name.value.trim(),
        last_name: formFields.last_name.value.trim(),
        email: formFields.email.value.trim(),
        subject: formFields.subject.value.trim(),
        message: formFields.message.value.trim(),
      });
      if (response?.success) {
        setFormFields(initialState);
        openSuccessNotification(
          response?.message || "Query submitted successfully",
        );
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes contactPulse {
            0%, 100% { opacity: 0.9; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.05); }
          }
        `}
      </style>
      <Box sx={classes.root}>
        <Box sx={classes.backgroundLayer}>
          <Box sx={classes.gradientOverlay} />
          <Box sx={classes.orb1} aria-hidden />
          <Box sx={classes.orb2} aria-hidden />
          <Box sx={classes.gridOverlay} />
        </Box>

        {/* Page header */}
        <Box sx={classes.headerBox}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography sx={classes.headerText}>{t("contactUs")}</Typography>
            <Typography sx={classes.subHeaderText}>
              We believe in providing excellent customer support. Reach out and
              we’ll get back to you soon.
            </Typography>
          </motion.div>
        </Box>

        <Container maxWidth="lg" sx={classes.contentWrapper}>
          {/* ——— Contact form + Website info side by side ——— */}
          <Box sx={classes.section} component="section">
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
              {/* Left: Contact form */}
              <Grid item xs={12} md={7}>
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  style={{ height: "100%" }}
                >
                  <Box
                    sx={{
                      ...classes.card,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "4px",
                      overflow: "hidden",
                      border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.2 : 0.2)}`,
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? `0 24px 64px ${alpha(theme.palette.common.black, 0.4)}`
                          : `0 24px 56px ${alpha(theme.palette.common.black, 0.08)}, 0 0 0 1px ${alpha(theme.palette.divider, 0.06)}`,
                    }}
                  >
                    <Box
                      sx={{
                        ...classes.cardInner,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          sx={{
                            ...classes.contactFormHeader,
                            fontSize: getRelativeFontSize(8),
                          }}
                        >
                          Let’s talk — Panacea Insights
                        </Typography>
                        <Typography sx={classes.contactFormSubheader}>
                          Fill in your details and we’ll respond as soon as we
                          can.
                        </Typography>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            required
                            label="First name"
                            name="first_name"
                            value={formFields.first_name.value}
                            onChange={handleChange}
                            error={!!formFields.first_name.error}
                            helperText={formFields.first_name.error}
                            variant="outlined"
                            fullWidth
                            sx={textFieldSx(theme)}
                            InputLabelProps={{
                              shrink: !!formFields.first_name.value,
                              sx: {
                                ...regularFont,
                                fontSize: getRelativeFontSize(0),
                                color: theme.palette.text.secondary,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            required
                            label="Last name"
                            name="last_name"
                            value={formFields.last_name.value}
                            onChange={handleChange}
                            error={!!formFields.last_name.error}
                            helperText={formFields.last_name.error}
                            variant="outlined"
                            fullWidth
                            sx={textFieldSx(theme)}
                            InputLabelProps={{
                              shrink: !!formFields.last_name.value,
                              sx: {
                                ...regularFont,
                                fontSize: getRelativeFontSize(0),
                                color: theme.palette.text.secondary,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            required
                            label={t("email")}
                            name="email"
                            type="email"
                            value={formFields.email.value}
                            onChange={handleChange}
                            error={!!formFields.email.error}
                            helperText={formFields.email.error}
                            variant="outlined"
                            fullWidth
                            sx={textFieldSx(theme)}
                            InputLabelProps={{
                              shrink: !!formFields.email.value,
                              sx: {
                                ...regularFont,
                                fontSize: getRelativeFontSize(0),
                                color: theme.palette.text.secondary,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            required
                            label="Subject"
                            name="subject"
                            value={formFields.subject.value}
                            onChange={handleChange}
                            error={!!formFields.subject.error}
                            helperText={formFields.subject.error}
                            variant="outlined"
                            fullWidth
                            sx={textFieldSx(theme)}
                            InputLabelProps={{
                              shrink: !!formFields.subject.value,
                              sx: {
                                ...regularFont,
                                fontSize: getRelativeFontSize(0),
                                color: theme.palette.text.secondary,
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            required
                            label={t("message")}
                            name="message"
                            value={formFields.message.value}
                            onChange={handleChange}
                            error={!!formFields.message.error}
                            helperText={formFields.message.error}
                            variant="outlined"
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={7}
                            sx={textFieldSx(theme)}
                            InputLabelProps={{
                              shrink: !!formFields.message.value,
                              sx: {
                                ...regularFont,
                                fontSize: getRelativeFontSize(0),
                                color: theme.palette.text.secondary,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                      <Box sx={{ ...classes.submitRow, mt: "auto", pt: 3 }}>
                        <Typography sx={classes.noteText}>
                          &#9432; {t("contactFormNote")}
                        </Typography>
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={isLoading}
                          endIcon={
                            !isLoading ? (
                              <EastIcon sx={{ fontSize: 18 }} />
                            ) : undefined
                          }
                          sx={{
                            ...classes.primaryButton,
                            textTransform: "none",
                            borderRadius: "12px",
                            px: 3,
                            background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
                            boxShadow: `0 4px 20px ${alpha(BRAND, 0.35)}`,
                            "&:hover": {
                              background: `linear-gradient(135deg, ${BRAND_LIGHT} 0%, ${BRAND} 100%)`,
                              boxShadow: `0 6px 24px ${alpha(BRAND, 0.45)}`,
                            },
                          }}
                        >
                          {isLoading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            "Submit"
                          )}
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              </Grid>

              {/* Right: Website info */}
              <Grid item xs={12} md={5}>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  style={{ height: "100%" }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      p: { xs: 2, sm: 3 },
                      borderRadius: "4px",
                      background: alpha(theme.palette.background.paper, 0.85),
                      border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      boxShadow:
                        theme.palette.mode === "dark"
                          ? `0 24px 64px ${alpha(theme.palette.common.black, 0.35)}`
                          : `0 24px 56px ${alpha(theme.palette.common.black, 0.06)}`,
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      overflow: "hidden",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "120px",
                        height: "120px",
                        background: `radial-gradient(circle at 100% 0%, ${alpha(BRAND, 0.12)} 0%, transparent 70%)`,
                        pointerEvents: "none",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        ...classes.sectionTitle,
                        fontSize: getRelativeFontSize(8),
                        mb: 0.5,
                      }}
                    >
                      Website info
                    </Typography>
                    <Typography
                      sx={{ ...classes.sectionSubtitle, mb: 3, mt: 1 }}
                    >
                      Explore our other initiatives and platforms.
                    </Typography>
                    <Stack spacing={2} sx={{ flex: 1 }}>
                      {OTHER_WEBSITES.map((site, index) => (
                        <motion.div
                          key={site.id}
                          initial={{ opacity: 0, y: 12 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.4,
                            delay: 0.25 + index * 0.1,
                          }}
                        >
                          <Box
                            sx={{
                              ...classes.websiteCard,
                              borderRadius: "4px",
                              overflow: "hidden",
                              cursor: "pointer",
                              height: "100%",
                              "&:hover": {
                                borderColor: alpha(BRAND, 0.5),
                                boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, theme.palette.mode === "dark" ? 0.3 : 0.1)}`,
                              },
                            }}
                            onClick={() => handleVisitWebsite(site.websiteUrl)}
                          >
                            <Box
                              component="img"
                              src={
                                theme.palette.mode === "dark"
                                  ? site.logoDark
                                  : site.logoLight
                              }
                              alt=""
                              sx={{
                                ...classes.websiteCardLogoImage,
                                height: 100,
                                p: 1.5,
                              }}
                            />
                            <Box sx={{ ...classes.websiteCardBody, pt: 1.5 }}>
                              <Typography sx={classes.websiteCardTitle}>
                                {site.name}
                              </Typography>
                              <Typography
                                sx={{
                                  ...classes.websiteCardDescription,
                                  fontSize: getRelativeFontSize(-1),
                                  display: "-webkit-box",
                                  WebkitLineClamp: 3,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {site.description}
                              </Typography>
                              <Typography
                                component="span"
                                sx={classes.websiteCardLink}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVisitWebsite(site.websiteUrl);
                                }}
                              >
                                Visit website{" "}
                                <EastIcon
                                  sx={{
                                    fontSize: 14,
                                    verticalAlign: "middle",
                                    ml: 0.25,
                                  }}
                                />
                              </Typography>
                            </Box>
                          </Box>
                        </motion.div>
                      ))}
                    </Stack>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Box>

          {/* ——— Section 3: Email for contact ——— */}
          <Box sx={classes.section} component="section">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
            >
              <Typography sx={classes.sectionTitle}>
                Email for contact
              </Typography>
              <Typography sx={classes.sectionSubtitle}>
                Prefer to write directly? Use the address below to say hello.
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{ marginTop: 24 }}
            >
              <Box sx={classes.emailStrip}>
                <Box sx={classes.emailStripInner}>
                  <Stack
                    direction="row"
                    spacing={3}
                    alignItems="center"
                    flex={1}
                  >
                    <Box sx={classes.emailStripIconWrap}>
                      <Box
                        component="img"
                        src={emailIcon}
                        alt=""
                        sx={{ height: 32, width: "auto" }}
                      />
                    </Box>
                    <Box>
                      <Typography sx={classes.emailStripLabel}>
                        {t("email")}
                      </Typography>
                      <Typography sx={classes.emailStripValue}>
                        {contactEmail}
                      </Typography>
                    </Box>
                  </Stack>
                  <Button
                    variant="outlined"
                    onClick={handleSayHello}
                    sx={classes.emailStripButton}
                    endIcon={<EastIcon sx={{ fontSize: 18 }} />}
                  >
                    Say hello
                  </Button>
                </Box>
              </Box>
            </motion.div>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Contact;
